import React, { useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { onLogout, setLoginSuccess } from '../../store/actions/reduxAction';
import { formatWithoutCurrency, hideCostForUser } from '../../helpers/helperFunction';
import { getRequest } from '../../helpers/apiHandler';
import elfoLogo from '../../assets/elfo_A2P.png';
import perkesoLogo from '../../assets/logo-perkeso.png';
import avatar from '../../assets/elfo_icon_new.svg';
import perkesoAvatar from '../../assets/PerkesoPP.png';

const baseUrl = process.env.PUBLIC_URL;
const white_label_perkeso = process.env.REACT_APP_WHITE_LABEL_PERKESO === 'true';

const UserAvatar = React.memo(({ profilePicture }) => {
  const profileAvatar = white_label_perkeso ? perkesoAvatar : avatar;
  return (
    <div className="user-profile nav-link pr-0">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
      >
        <circle
          cx="20"
          cy="20"
          r="18.5"
          stroke="url(#paint0_linear_71_4800)"
          strokeWidth="3"
        />
        <defs>
          <linearGradient
            id="paint0_linear_71_4800"
            x1="20"
            y1="0"
            x2="20"
            y2="40"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#2D57A8" />
            <stop offset="1" stopColor="#2DA858" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      <img
        src={profilePicture === null
          ? profileAvatar
          : `data:image/png;base64,${profilePicture}`}
        className={profilePicture === null ? '' : 'with-avatar'}
        alt="Avatar"
      />
    </div>
  );
});

const UserMenu = React.memo(({ name, handleRemove }) => (
  <>
    <div
      className="nav-link dropdown-toggle user-profile-dropdown"
      href="#"
      data-toggle="dropdown"
      aria-expanded="false"
      style={{ cursor: 'pointer' }}
    >
      <div className="username">
        <span>{name}</span>
        <i className="fas fa-chevron-down ml-2" />
      </div>
    </div>
    <div
      className="dropdown-menu name-initials-dropdown"
      aria-labelledby="navbarDropdown"
    >
      <Link className="dropdown-item" to="/dashboard/account/personal-info">
        Account Settings
      </Link>
      {!white_label_perkeso && (
        <Link className="dropdown-item" to="/dashboard/account/billing-log">
          Account Billing
        </Link>
      )}
      <div className="dropdown-divider" />
      <button className="dropdown-item dropdown-item-logout" onClick={handleRemove}>
        <i className="fas fa-sign-out-alt mr-2" />
        Logout
      </button>
    </div>
  </>
));

const CreditBalance = React.memo(({ balance, currency }) => {
  const formattedBalance = useMemo(() => {
    if (white_label_perkeso) return formatWithoutCurrency(balance || 0, 0, 0);
    return `${currency.symbol} ${formatWithoutCurrency(balance || 0, 2)}`
  },
    [currency.symbol, balance]
  );

  return (
    <div className="trial-credits">
      <span>CREDIT BALANCE</span>
      <span
        className={parseFloat(balance) > 0 ? 'text-success' : 'text-error'}
      >
        {formattedBalance}
      </span>
    </div>
  );
});

const FeatureButtons = React.memo(() => {
  const { email } = useSelector((state) => state.auth.user);
  return (
  <>
    {!hideCostForUser(email) && (
      <Button variant="ghost" href="/dashboard/sms-price-list" style={{ minWidth: 120 }}>
        SMS Pricing
      </Button>
    )}
    <Button
      variant="primary"
      href="/dashboard/payment/buy-credits"
      style={{ minWidth: 120 }}
    >
      Top Up
    </Button>
  </>
  );
});

const Topbar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { access_token, available_balance, name, currency, profile_picture, role } = useSelector((state) => state.auth.user);
  const { reload_top_bar } = useSelector((state) => state.topbar);

  const handleRemove = useCallback(() => {
    dispatch(onLogout());
    localStorage.clear();
    navigate('/login');
  }, [dispatch, navigate]);

  const check_if_logged_in = () => {
    if (!access_token) return navigate('/login');
    if (role === 'manager') return navigate('/manager/dashboard');
    if (role === 'admin') return navigate('/admin/dashboard');

    const data = { headers: { Authorization: access_token.token } };
    getRequest('/user/me', data, (res) => {
      if (res.success) {
        dispatch(setLoginSuccess(res));
      }
      if (res.error) {
        return navigate('/login');
      }
    });
  }

  useEffect(() => {
    check_if_logged_in();
  }, [reload_top_bar]);

  return (
    <div className="topbar">
      <div className="top-bar-component-container pb-1">
        <nav className="navbar navbar-expand navbar-light pt-0 pb-0">
          <Link className={white_label_perkeso ? "navbar-brand-p" : "navbar-brand"} to={`${baseUrl}/dashboard`}>
            <img className="elfoLogo" alt="background-vector" src={white_label_perkeso ? perkesoLogo : elfoLogo} />
          </Link>
          <div className="collapse navbar-collapse" id="navbarSupportedContent">
            <ul className="navbar-nav">
              <li className="nav-item account-status">
                {!white_label_perkeso && <FeatureButtons />}
                <CreditBalance balance={available_balance} currency={currency} />
              </li>
              <li className="nav-item dropdown">
                <UserAvatar profilePicture={profile_picture} />
                <UserMenu name={name} handleRemove={handleRemove} />
              </li>
            </ul>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default React.memo(Topbar);
